// Low-poly people: 18 rigged bodies (normal/fat/strong × man/woman × a/b/c) with nine palette textures.
// The models ship in a T-pose with no clips, so walking, waving and tumbling are driven procedurally on
// the bones every frame. Cheap enough for the ~70 pedestrians near the player.

import { Scene } from '@babylonjs/core/scene';
import { LoadAssetContainerAsync } from '@babylonjs/core/Loading/sceneLoader';
import { AssetContainer } from '@babylonjs/core/assetContainer';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { Quaternion } from '@babylonjs/core/Maths/math.vector';
import { AnimationGroup } from '@babylonjs/core/Animations/animationGroup';
import type { CascadedShadowGenerator } from '@babylonjs/core/Lights/Shadows/cascadedShadowGenerator';
import { ASSET_BASE } from './config';

export const PEOPLE_VARIANTS = ['normal-man-a', 'normal-man-b', 'normal-man-c', 'normal-woman-a', 'normal-woman-b', 'normal-woman-c', 'fat-man-a', 'fat-man-b', 'fat-man-c', 'fat-woman-a', 'fat-woman-b', 'fat-woman-c', 'strong-man-a', 'strong-man-b', 'strong-man-c', 'strong-woman-a', 'strong-woman-b', 'strong-woman-c'];
export const PALETTES = 9;

const BONES = ['Stomach', 'Chest', 'Head', 'UpperArm.L', 'LowerArm.L', 'UpperArm.R', 'LowerArm.R', 'UpperLeg.L', 'LowerLeg.L', 'UpperLeg.R', 'LowerLeg.R'] as const;
type BoneName = (typeof BONES)[number];

export type Clip = 'Walk' | 'Jog' | 'Sprint' | 'Idle' | 'Talk' | 'Dance' | 'Hit' | 'Fall' | 'Sit' | 'Drive' | 'Wave' | 'Push';

export class PeopleLibrary {
  private constructor(readonly scene: Scene, private containers: AssetContainer[], private materials: PBRMaterial[], readonly clips: Map<string, AnimationGroup>) {}

  static async load(scene: Scene): Promise<PeopleLibrary> {
    const containers = await Promise.all(PEOPLE_VARIANTS.map((v) => LoadAssetContainerAsync(`${ASSET_BASE}models/people/${v}.glb`, scene)));
    // one material per palette, shared by every body
    const base = containers[0].materials.find((m) => m instanceof PBRMaterial) as PBRMaterial | undefined;
    const materials: PBRMaterial[] = [];
    for (let i = 1; i <= PALETTES; i++) {
      const m = base ? base.clone(`people_palette${i}`) : new PBRMaterial(`people_palette${i}`, scene);
      const tex = new Texture(`${ASSET_BASE}models/people/palette${i}.png`, scene, true, false, Texture.NEAREST_SAMPLINGMODE);
      m.albedoTexture = tex; m.metallic = 0; m.roughness = 1; m.backFaceCulling = false;
      materials.push(m);
    }
    // retargeted Quaternius clips, exported on the normal-man-a rig; applied to any body by bone name
    const clips = new Map<string, AnimationGroup>();
    try {
      const anims = await LoadAssetContainerAsync(`${ASSET_BASE}models/people/anims.glb`, scene);
      for (const g of anims.animationGroups) clips.set(g.name, g);
      for (const m of anims.meshes) m.setEnabled(false);
    } catch { /* no clips: procedural fallback */ }
    return new PeopleLibrary(scene, containers, materials, clips);
  }

  /** Bind one clip to an instance's bones (matched by node name). Done lazily, per clip actually played. */
  bindClip(root: TransformNode, clip: Clip): AnimationGroup | null {
    const g = this.clips.get(clip); if (!g) return null;
    const byName = new Map<string, TransformNode>();
    for (const n of root.getDescendants(false)) byName.set(n.name, n as TransformNode);
    const c = g.clone(`${clip}_${root.uniqueId}`, (old) => byName.get((old as TransformNode).name) ?? null, false);
    for (let i = c.targetedAnimations.length - 1; i >= 0; i--) if (!c.targetedAnimations[i].target) c.targetedAnimations.splice(i, 1);
    if (!c.targetedAnimations.length) { c.dispose(); return null; }
    return c;
  }

  spawn(variant: number, palette: number, shadows?: CascadedShadowGenerator | null): PersonView {
    const c = this.containers[((variant % PEOPLE_VARIANTS.length) + PEOPLE_VARIANTS.length) % PEOPLE_VARIANTS.length];
    const inst = c.instantiateModelsToScene((n) => n, false, { doNotInstantiate: true });
    const root = inst.rootNodes[0] as TransformNode;
    const holder = new TransformNode('person', this.scene);
    // a plain pivot between holder and the glTF root: the root carries a rotation quaternion, so Euler poses
    // (tumble, lie) must go on a node of their own
    const pivot = new TransformNode('pose', this.scene); pivot.parent = holder; root.parent = pivot;
    const bones = new Map<BoneName, TransformNode>();
    for (const n of root.getDescendants(false)) if ((BONES as readonly string[]).includes(n.name)) bones.set(n.name as BoneName, n as TransformNode);
    const mat = this.materials[((palette % PALETTES) + PALETTES) % PALETTES];
    for (const m of root.getChildMeshes(false)) { m.material = mat; m.isPickable = false; m.receiveShadows = true; shadows?.addShadowCaster(m, false); }
    return new PersonView(holder, pivot, bones, inst.skeletons.length ? inst : null, this.clips.size ? this : null);
  }
}

export class PersonView {
  private rest = new Map<BoneName, Quaternion>();
  private phase = Math.random() * Math.PI * 2;
  private current: Clip | null = null;
  private groups = new Map<Clip, AnimationGroup>();
  private paused = false;
  constructor(readonly root: TransformNode, readonly model: TransformNode, private bones: Map<BoneName, TransformNode>, private inst: { dispose(): void } | null, private lib: PeopleLibrary | null) {
    for (const [k, n] of bones) this.rest.set(k, (n.rotationQuaternion ?? Quaternion.Identity()).clone());
  }

  get hasClips(): boolean { return !!this.lib; }

  /** Play a clip (looping unless told otherwise); speed scales playback. Clips are cloned on first use. */
  play(clip: Clip, speed = 1, loop = true): boolean {
    if (!this.lib) return false;
    let g = this.groups.get(clip);
    if (!g) { const c = this.lib.bindClip(this.root, clip); if (!c) return false; g = c; this.groups.set(clip, g); }
    if (this.current !== clip || this.paused || !g.isPlaying) {
      for (const [k, o] of this.groups) if (k !== clip) o.stop();
      const resume = this.current === clip && g.isStarted;
      g.speedRatio = speed;
      // start() is a no-op on a paused group. play() resumes its animatables.
      g.play(loop);
      if (!resume && g.to > g.from) g.goToFrame(g.from + (this.phase / (Math.PI * 2)) * (g.to - g.from));
      this.current = clip; this.paused = false;
    } else if (g.speedRatio !== speed) g.speedRatio = speed;
    return true;
  }

  /** Hide and stop animating (far from the camera); play() resumes. */
  cull(hidden: boolean): void {
    if (hidden === !this.root.isEnabled()) return;
    this.root.setEnabled(!hidden);
    if (hidden) { for (const g of this.groups.values()) if (g.isPlaying) g.pause(); this.paused = true; }
  }

  private set(bone: BoneName, x: number, y: number, z: number): void {
    const n = this.bones.get(bone); const r = this.rest.get(bone); if (!n || !r) return;
    n.rotationQuaternion = r.multiply(Quaternion.FromEulerAngles(x, y, z));
  }

  place(x: number, y: number, z: number, yaw: number): void { this.root.position.set(x, y, z); this.root.rotation.set(0, yaw, 0); }

  /** Walk cycle: legs and arms swing with speed; arms hang at the sides instead of the T-pose. */
  walk(time: number, speed: number): void {
    const t = time * (4 + speed * 3) + this.phase; const amp = Math.min(0.7, 0.25 + speed * 0.35);
    const swing = Math.sin(t) * amp;
    this.set('UpperLeg.L', swing, 0, 0); this.set('UpperLeg.R', -swing, 0, 0);
    this.set('LowerLeg.L', Math.max(0, -Math.sin(t)) * 0.9 * amp, 0, 0); this.set('LowerLeg.R', Math.max(0, Math.sin(t)) * 0.9 * amp, 0, 0);
    this.set('UpperArm.L', -swing * 0.8, 0, 1.25); this.set('UpperArm.R', swing * 0.8, 0, -1.25);
    this.set('LowerArm.L', -0.3, 0, 0); this.set('LowerArm.R', -0.3, 0, 0);
    this.set('Chest', 0, Math.sin(t) * 0.05, 0);
    this.root.position.y += Math.abs(Math.sin(t)) * 0.03;
  }

  /** Standing still, arms down, slight sway. */
  idle(time: number): void {
    const t = time + this.phase;
    this.set('UpperLeg.L', 0, 0, 0); this.set('UpperLeg.R', 0, 0, 0); this.set('LowerLeg.L', 0, 0, 0); this.set('LowerLeg.R', 0, 0, 0);
    this.set('UpperArm.L', 0.05, 0, 1.3 + Math.sin(t * 1.3) * 0.03); this.set('UpperArm.R', 0.05, 0, -1.3 - Math.sin(t * 1.3) * 0.03);
    this.set('LowerArm.L', -0.25, 0, 0); this.set('LowerArm.R', -0.25, 0, 0);
    this.set('Chest', Math.sin(t * 1.3) * 0.02, 0, 0);
  }

  /** Waving one arm overhead: a passenger hailing the cab. */
  wave(time: number): void {
    this.idle(time);
    const t = time * 6 + this.phase;
    this.set('UpperArm.R', 0, 0, -2.9 + Math.sin(t) * 0.25);
    this.set('LowerArm.R', 0, 0, -0.6 + Math.sin(t) * 0.5);
    this.set('Head', 0, Math.sin(t * 0.5) * 0.2, 0);
  }

  /** Bowled over: the whole body tumbles. */
  tumble(dt: number): void {
    // spin about hip height: the pivot sits 0.9 m up (see StreetFx), so drop the body under it
    for (const c of this.model.getChildTransformNodes(true)) c.position.y = -0.9;
    this.model.rotation.x += dt * 9; this.model.rotation.z += dt * 5;
    this.set('UpperArm.L', 0, 0, 0.3); this.set('UpperArm.R', 0, 0, -0.3); this.set('UpperLeg.L', 0.6, 0, 0); this.set('UpperLeg.R', -0.6, 0, 0);
  }
  resetPose(): void { this.model.rotation.set(0, 0, 0); for (const c of this.model.getChildTransformNodes(true)) c.position.y = 0; }

  /** Stop every clip so the procedural poses (tumble, lie) own the bones again. */
  stopClips(): void { for (const g of this.groups.values()) g.stop(); this.current = null; this.paused = false; }

  /** Flat on the ground after being hit: body laid along the floor, limbs loose. */
  lie(): void {
    this.stopClips();
    for (const c of this.model.getChildTransformNodes(true)) c.position.y = 0;
    this.model.rotation.set(-Math.PI / 2, 0, 0.2);
    this.set('UpperArm.L', 0, 0, 1.0); this.set('UpperArm.R', 0, 0, -1.6); this.set('LowerArm.L', -0.4, 0, 0); this.set('LowerArm.R', -0.2, 0, 0);
    this.set('UpperLeg.L', 0.25, 0, 0.15); this.set('UpperLeg.R', -0.1, 0, -0.1); this.set('LowerLeg.L', 0.5, 0, 0); this.set('LowerLeg.R', 0.1, 0, 0);
    this.set('Head', 0.3, 0.4, 0);
  }

  setEnabled(v: boolean): void { this.root.setEnabled(v); }
  meshes(): AbstractMesh[] { return this.model.getChildMeshes(false); }
  dispose(): void { for (const g of this.groups.values()) g.dispose(); this.inst?.dispose(); this.root.dispose(false, false); }
}
